import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { BookOpen, User, Mail, Phone, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().trim().email({ message: 'Invalid email format' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

const signupSchema = z.object({
  fullName: z.string().trim().min(1, { message: 'Full name is required' }).max(100),
  email: z.string().trim().email({ message: 'Invalid email format' }).max(255),
  mobile: z.string().regex(/^[6-9]\d{9}$/, { message: 'Invalid mobile number' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

const Auth: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { signIn, signUp, user, isLoading: authLoading } = useSupabaseAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (isLogin) {
      const result = loginSchema.safeParse({ email, password });
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }
    } else {
      const result = signupSchema.safeParse({ fullName, email, mobile, password });
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
        return;
      }
    }
    
    setIsLoading(true);
    
    if (isLogin) {
      const { error } = await signIn(email, password);
      if (!error) {
        toast({
          title: "Welcome back!",
          description: "You have successfully logged in.",
        });
        navigate('/');
      } else {
        toast({
          title: "Login Failed",
          description: error.message || "Invalid email or password.",
          variant: "destructive",
        });
      }
    } else {
      const { error, data: authData } = await signUp(email, password, fullName, mobile);
      if (!error) {
        // Save password to database for displaying on dashboard
        if (authData?.user?.id) {
          supabase.from('profiles')
            .update({ auto_password: password })
            .eq('id', authData.user.id)
            .then(() => {});
        }
        
        toast({
          title: "Account Created!",
          description: "Welcome to Toppers Buddy!",
        });
        navigate('/');
      } else {
        let errorMessage = "An error occurred during registration.";
        if (error.message.includes('already registered')) {
          errorMessage = "An account with this email already exists.";
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast({
          title: "Registration Failed",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
    
    setIsLoading(false);
  };

  return (
    <>
      <SEOHead 
        title={isLogin ? 'Login - TOPPERS BUDDY | Class 10th Science Notes' : 'Sign Up - TOPPERS BUDDY | Class 10th Science Notes'}
        description="Login or create account to access toppers handwritten notes for class 10th science. Best notes for class 10th science board exam preparation."
        noIndex={true}
      />

      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="mb-6 gap-2"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>

          {/* Auth Card */}
          <div className="rounded-2xl border border-border/50 bg-card p-8 shadow-card">
            {/* Logo */}
            <div className="mb-6 flex items-center justify-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-600">
                <BookOpen className="h-6 w-6 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">
                <span className="text-gradient">TOPPERS</span>
                <span className="text-foreground"> BUDDY</span>
              </span>
            </div>

            {/* Tabs */}
            <div className="mb-6 flex rounded-lg bg-secondary p-1">
              <button
                className={`flex-1 rounded-md py-2 text-sm font-medium transition-all ${
                  isLogin 
                    ? 'bg-card text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setIsLogin(true)}
              >
                Login
              </button>
              <button
                className={`flex-1 rounded-md py-2 text-sm font-medium transition-all ${
                  !isLogin 
                    ? 'bg-card text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => setIsLogin(false)}
              >
                Sign Up
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      Full Name
                    </Label>
                    <Input
                      id="fullName"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className={errors.fullName ? 'border-destructive' : ''}
                      disabled={isLoading}
                    />
                    {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobile" className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      WhatsApp Mobile Number
                    </Label>
                    <Input
                      id="mobile"
                      placeholder="Enter 10-digit mobile number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={errors.mobile ? 'border-destructive' : ''}
                      disabled={isLoading}
                    />
                    {errors.mobile && <p className="text-xs text-destructive">{errors.mobile}</p>}
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email ID
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={errors.email ? 'border-destructive' : ''}
                  disabled={isLoading}
                />
                {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={isLogin ? 'Enter your password' : 'Create a password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={errors.password ? 'border-destructive' : ''}
                  disabled={isLoading}
                />
                {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
              </div>

              <Button 
                type="submit" 
                variant="hero" 
                className="w-full" 
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isLogin ? 'Logging in...' : 'Creating Account...'}
                  </>
                ) : (
                  isLogin ? 'Login' : 'Create Account'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Auth;
